import {Box, IconButton} from '@mui/material';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import React, {useRef} from 'react';
import parseHTML from 'html-react-parser';
import {useReactToPrint} from 'react-to-print';
import PrintIcon from '@mui/icons-material/Print';
import moment from 'moment';
import {
  GetHandoverNotes,
  GetHandoverNotes_reviewDetails_notes,
} from '../../schema-types/GetHandoverNotes';
import {useAccountContext} from '../../contexts/AccountContext';
import {SaltusAny} from '../../utils/SaltusAny';
import {Button} from '../../components/Button/Button';
import {GetPreviousNotes} from '../../schema-types/GetPreviousNotes';
import {sortNotesByCategory} from './sortNotesByCategory';

const classes = {
  root: {
    margin: '1rem',
  },
  btn: {
    margin: 0,
  },
  header2: {
    display: 'flex',
    alignItems: 'center',
  },
};

interface ReviewNotesPrintedProps extends React.HTMLProps<any> {
  handoverData?: GetHandoverNotes;
}

interface AccountNotesPrintedProps extends React.HTMLProps<any> {
  previousNotes?: GetPreviousNotes['previousNotes'];
}

const stringOrNA = (value: SaltusAny) =>
  typeof value === 'string' && value !== '' ? value : 'N/A';

const formattedDate = (value: string) => moment.utc(value).format('DD/MM/YYYY');

const PrintableNote: React.FC<{
  note: GetHandoverNotes_reviewDetails_notes;
}> = ({note}) => {
  return (
    <>
      <div
        style={{
          display: 'flex',
        }}
      >
        <div style={{width: '160px'}}>
          <b>{note.displayName}:</b>
        </div>

        <div style={{flex: 1}}>
          {note.markupBody ? parseHTML(note.markupBody) : stringOrNA(note.body)}
        </div>
      </div>
      <hr color="lightgrey" />
    </>
  );
};

const PrintStyles = () => {
  const getPageMargins = () => {
    return `@page { margin: 1.5em 1.5em 1.5em 1.5em !important; }`;
  };
  return <style>{getPageMargins()}</style>;
};

export const ReviewNotesPrinted = React.forwardRef<any, ReviewNotesPrintedProps>(
  (props, ref) => {
    const {handoverData} = props;
    const {account} = useAccountContext();

    return (
      <div ref={ref} style={classes.root}>
        <PrintStyles />
        <img
          style={{
            width: '100px',
            height: '25px',
            objectFit: 'contain',
            filter: `grayscale(100%) contrast(200%) invert(100%)`,
          }}
          alt="company logo"
          src="/logo.svg"
        />
        <h2
          style={{
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
          }}
        >
          {' '}
          Notes for {account?.name}
        </h2>
        <h2 style={classes.header2}>
          <FindInPageIcon style={{margin: '0 8px'}} /> Current Review:
        </h2>
        <h3
          style={{
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
          }}
        >
          {`${stringOrNA(handoverData?.reviewDetails?.name)} - ${formattedDate(
            handoverData?.reviewDetails?.dateBooked
          )}`}
        </h3>
        {sortNotesByCategory(handoverData?.reviewDetails?.notes ?? []).map((note) => {
          return <PrintableNote note={note} key={note.id} />;
        })}
        <div className="no-break" style={{marginTop: '3em'}}>
          <h2 style={classes.header2}>
            <AssignmentTurnedInIcon style={{margin: '0 8px'}} />
            Previous Reviews:
          </h2>
          {handoverData?.previousNotes?.map((review) => {
            if (review.id === handoverData.reviewDetails?.id) {
              return <div key={review.id} />;
            }
            return (
              <div className="no-break" style={{marginTop: '3em'}} key={review.id}>
                <h3
                  style={{
                    textAlign: 'center',
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  {`${stringOrNA(review?.name)} - ${formattedDate(review?.dateBooked)}`}
                </h3>
                {sortNotesByCategory(review.notes ?? []).map((note) => {
                  return <PrintableNote note={note} key={note.id} />;
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

export const AccountNotesPrinted = React.forwardRef<any, AccountNotesPrintedProps>(
  (props, ref) => {
    const {previousNotes} = props;
    const {account} = useAccountContext();

    return (
      <div ref={ref} style={classes.root}>
        <PrintStyles />
        <h1
          style={{
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
          }}
        >
          <img
            style={{
              width: '100px',
              height: '25px',
              objectFit: 'contain',
              filter: `grayscale(100%) contrast(200%) invert(100%)`,
            }}
            alt="company logo"
            src="/logo.svg"
          />{' '}
          Notes for {account?.name}
        </h1>
        {previousNotes?.map((review) => {
          return (
            <div style={{marginTop: '3em'}} key={review.id}>
              <h3
                style={{
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5',
                }}
              >
                {`${stringOrNA(review?.name)} - ${formattedDate(review?.dateBooked)}`}
              </h3>
              {sortNotesByCategory(review.notes ?? []).map((note) => {
                return <PrintableNote note={note} key={note.id} />;
              })}
            </div>
          );
        })}
      </div>
    );
  }
);

export const ReviewPrintableNotes = ({handoverData}: ReviewNotesPrintedProps) => {
  const componentRef = useRef<React.ReactInstance | null>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (
    <Box position="absolute" top={0} right={0}>
      <div style={{display: 'none'}}>
        <ReviewNotesPrinted ref={componentRef} handoverData={handoverData} />
      </div>
      <Box display="flex" alignItems="center">
        <Button
          startIcon={
            <IconButton
              aria-label="print"
              sx={classes.btn}
              color="secondary"
              size="large"
            >
              <PrintIcon />
            </IconButton>
          }
          eventName="Print all notes"
          onClick={handlePrint}
          variant="text"
        >
          Print all notes
        </Button>
      </Box>
    </Box>
  );
};

export const AccountPrintableNotes = ({previousNotes}: AccountNotesPrintedProps) => {
  const componentRef = useRef<React.ReactInstance | null>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (
    <div>
      <div style={{display: 'none'}}>
        <AccountNotesPrinted ref={componentRef} previousNotes={previousNotes} />
      </div>
      <Box display="flex" alignItems="center">
        <Button
          startIcon={
            <IconButton
              aria-label="print"
              sx={classes.btn}
              color="secondary"
              size="large"
            >
              <PrintIcon />
            </IconButton>
          }
          eventName="Print all notes"
          onClick={handlePrint}
          variant="text"
        >
          Print all notes
        </Button>
      </Box>
    </div>
  );
};
